//
// # DrcErrorRow
//
// Single error row in the DRC panel.
// Clicking highlights the relevant layout instances.
//

import React from "react";
import { Layers, AlertTriangle, Info, Ruler } from "lucide-react";
import { useWorkspace } from "../../context";
import type { DrcError } from "./types";

// Maximum instances to show before truncating with "+N more"
const MAX_INSTANCES_SHOWN = 5;

interface DrcErrorRowProps {
  error: DrcError;
  index: number;
}

// Icon and color for each error kind
const errorStyles: Record<
  string,
  { icon: React.ReactNode; bgClass: string; iconClass: string }
> = {
  instance_overlap: {
    icon: <Layers size={12} />,
    bgClass: "bg-red-500/10 hover:bg-red-500/20",
    iconClass: "text-red-400",
  },
  min_spacing: {
    icon: <Ruler size={12} />,
    bgClass: "bg-orange-500/10 hover:bg-orange-500/20",
    iconClass: "text-orange-400",
  },
  min_width: {
    icon: <Ruler size={12} />,
    bgClass: "bg-yellow-500/10 hover:bg-yellow-500/20",
    iconClass: "text-yellow-400",
  },
  parameter_violation: {
    icon: <Info size={12} />,
    bgClass: "bg-blue-500/10 hover:bg-blue-500/20",
    iconClass: "text-blue-400",
  },
};

const defaultStyle = {
  icon: <AlertTriangle size={12} />,
  bgClass: "bg-gray-500/10 hover:bg-gray-500/20",
  iconClass: "text-gray-400",
};

export function DrcErrorRow({ error, index }: DrcErrorRowProps) {
  const workspace = useWorkspace();
  const style = errorStyles[error.kind] ?? defaultStyle;

  const handleClick = () => {
    workspace.highlightDrcError(error);
  };

  // Format the error label with truncation for long instance lists
  const formatLabel = (): string => {
    if (error.instances && error.instances.length > 0) {
      // Filter out empty strings
      const validInstances = error.instances.filter((i) => i.length > 0);
      if (validInstances.length === 0) {
        // Fall through to other label options
      } else if (validInstances.length <= MAX_INSTANCES_SHOWN) {
        return validInstances.join(", ");
      } else {
        // Truncate long lists: show first N + count
        const shown = validInstances.slice(0, MAX_INSTANCES_SHOWN).join(", ");
        const remaining = validInstances.length - MAX_INSTANCES_SHOWN;
        return `${shown} +${String(remaining)} more`;
      }
    }
    if (error.ruleId) {
      return error.ruleId;
    }
    return `Error ${String(index + 1)}`;
  };

  // Severity badge color
  const severityColors = {
    error: "bg-red-500/30 text-red-400",
    warning: "bg-yellow-500/30 text-yellow-400",
    info: "bg-blue-500/30 text-blue-400",
  };

  return (
    <button
      className={`w-full px-2 py-1.5 rounded flex items-center gap-2 text-left transition-colors cursor-pointer ${style.bgClass}`}
      onClick={handleClick}
    >
      <span className={style.iconClass}>{style.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium truncate">
            {formatLabel()}
          </span>
          <span
            className={`text-[9px] px-1 py-0.5 rounded ${
              severityColors[error.severity]
            }`}
          >
            {error.severity}
          </span>
        </div>
        <div className="text-[10px] text-[var(--color-ide-text-dim)] truncate">
          {error.message}
        </div>
      </div>
    </button>
  );
}
